import { AppointmentService } from '@avanta/shared/services/appointments';
import { type TopPsychologist } from '@avanta/shared/types/appointments';
import { extractTimeFromUnified, formatDateISO } from '@avanta/shared/utils/date';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { getUserId } from 'utils/storage';
import { AppointmentsReservePayload } from '../../../types';

interface UseFetchTopPsychologistsReturn {
  psychologists: TopPsychologist[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
}

export const useFetchTopPsychologists = (): UseFetchTopPsychologistsReturn => {
  const { watch } = useFormContext();
  const { state } = useLocation();
  const patientId = state?.patientId || getUserId();

  const [centerId, specialityId, timeZone, language, dateTime] = watch([
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.timezone,
    AppointmentsReservePayload.language,
    AppointmentsReservePayload.dateTime,
  ]);

  const selectedDate = dateTime ? formatDateISO(dateTime, "yyyy-MM-dd'T'HH:mm:ss") : '';
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const isCalendarQueryEnabled =
    !!specialityId &&
    !!centerId &&
    !!timeZone &&
    !!language &&
    !!selectedDate &&
    !!selectedTime &&
    !!patientId;

  const {
    data: psychologists,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_TOP_PSYCHOLOGISTS_AVAILABILITY,
      patientId,
      selectedDate,
      selectedTime,
      specialityId,
      centerId,
      language,
    ],
    queryFn: () =>
      AppointmentService.getTopPsychologistsAvailability({
        startDate: selectedDate,
        days: 0,
        specialityId,
        centerId,
        patientId,
        spokenLanguage: language,
        slotHour: selectedTime,
      }),
    select: response => response.data,
    enabled: isCalendarQueryEnabled,
    placeholderData: keepPreviousData,
  });

  return { psychologists, isLoading, isFetching };
};
